import React, { useState, useCallback, useEffect } from "react";
import {
  Datagrid,
  List,
  NumberField,
  FunctionField,
  TextField,
  DateField,
  TopToolbar,
  useListContext,
} from "react-admin";
import {
  Box,
  Button,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
  Drawer,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { ViewColumn, Download, FilterList } from "@mui/icons-material";
import * as XLSX from "xlsx";
import Parse from "parse";
import CustomPagination from "../../Components/CustomPagination";

// Column definitions grouped by category
const COLUMN_GROUPS = {
  "Player Metrics": [
    {
      id: "totalPlayerRecharge",
      label: "Total Player Recharge",
      default: true,
    },
    { id: "totalPlayerRedeem", label: "Total Player Redeem", default: true },
  ],
  "Agent Information": [
    { id: "agentName", label: "Agent Name", default: true },
    {
      id: "agentFirstRechargeDate",
      label: "First Recharge Date",
      default: true,
    },
    {
      id: "agentFirstRechargeAmount",
      label: "First Recharge Amount",
      default: true,
    },
    {
      id: "agentFirstRechargeDoneBy",
      label: "First Recharge Done By",
      default: true,
    },
    { id: "agentTotalRecharge", label: "Total Recharge", default: true },
    { id: "agentFreeCredit", label: "Free Credit", default: true },
    { id: "agentRemainingCredit", label: "Remaining Credit", default: true },
    {
      id: "agentLastRechargeDate",
      label: "Last Recharge Date",
      default: true,
    },
    { id: "agentLastRedeemDate", label: "Last Redeem Date", default: false },
    { id: "agentCurrentBalance", label: "Current Balance", default: true },
  ],
  "Calculated Metrics": [
    { id: "agentIncome", label: "Income", default: true },
    { id: "profitLossStatus", label: "Profit/Loss", default: true },
    { id: "agentHolding", label: "Holding %", default: true },
  ],
  "Sub-Distributor": [
    { id: "subDistributorName", label: "Sub Distributor Name", default: false },
    {
      id: "subDistributorFirstRechargeDate",
      label: "Sub Distributor First Recharge Date",
      default: false,
    },
    {
      id: "subDistributorFirstRechargeAmount",
      label: "Sub Distributor First Recharge Amount",
      default: false,
    },
    {
      id: "subDistributorFirstRechargeDoneBy",
      label: "Sub Distributor First Recharge Done By",
      default: false,
    },
    {
      id: "subDistributorTotalRecharge",
      label: "Sub Distributor Total Recharge",
      default: false,
    },
    {
      id: "subDistributorCurrentBalance",
      label: "Sub Distributor Current Balance",
      default: false,
    },
  ],
  Distributor: [
    { id: "distributorName", label: "Distributor Name", default: false },
    {
      id: "distributorFirstRechargeDate",
      label: "Distributor First Recharge Date",
      default: false,
    },
    {
      id: "distributorFirstRechargeAmount",
      label: "Distributor First Recharge Amount",
      default: false,
    },
    {
      id: "distributorFirstRechargeDoneBy",
      label: "Distributor First Recharge Done By",
      default: false,
    },
    { id: "distributorTotalRecharge", label: "Distributor Total Recharge", default: false },
    {
      id: "distributorCurrentBalance",
      label: "Distributor Current Balance",
      default: false,
    },
  ],
  "Master-Distributor": [
    { id: "masterDistributorName", label: "Master Distributor Name", default: false },
    {
      id: "masterDistributorFirstRechargeDate",
      label: "First Recharge Date",
      default: false,
    },
    {
      id: "masterDistributorFirstRechargeAmount",
      label: "Master Distributor First Recharge Amount",
      default: false,
    },
    {
      id: "masterDistributorFirstRechargeDoneBy",
      label: "Master Distributor First Recharge Done By",
      default: false,
    },
    {
      id: "masterDistributorTotalRecharge",
      label: "Master Distributor Total Recharge",
      default: false,
    },
    {
      id: "masterDistributorCurrentBalance",
      label: "Master Distributor Current Balance",
      default: false,
    },
  ],
  "Game-Owner": [
    { id: "gameOwnerName", label: "Game Owner Name", default: false },
    {
      id: "gameOwnerFirstRechargeDate",
      label: "Game Owner First Recharge Date",
      default: false,
    },
    {
      id: "gameOwnerFirstRechargeAmount",
      label: "Game Owner First Recharge Amount",
      default: false,
    },
    {
      id: "gameOwnerFirstRechargeDoneBy",
      label: "Game Owner First Recharge Done By",
      default: false,
    },
    { id: "gameOwnerTotalRecharge", label: "Game Owner Total Recharge", default: false },
    { id: "gameOwnerCurrentBalance", label: "Game Owner Current Balance", default: false },     
  ],
  "Super User": [{ id: "superUserName", label: "Super User", default: false }],
};

// Custom Filter Component
const CustomFilter = ({ onSearchChange, onFiltersChange }) => {
  const { setFilters } = useListContext();
  const [searchValue, setSearchValue] = useState("");
  const [agentSuggestions, setAgentSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [dateRange, setDateRange] = useState("all");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [holdingRange, setHoldingRange] = useState("all");
  const [customHoldingMin, setCustomHoldingMin] = useState("");
  const [customHoldingMax, setCustomHoldingMax] = useState("");
  const [parentFilter, setParentFilter] = useState(null);
  const [parentSearchValue, setParentSearchValue] = useState("");
  const [parentSuggestions, setParentSuggestions] = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);

  // Fetch agent suggestions for autocomplete
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchValue.length < 2) {
        setAgentSuggestions([]);
        return;
      }

      setLoadingSuggestions(true);
      try {
        const result = await Parse.Cloud.run("searchAgentNames", {
          search: searchValue,
        });
        if (result.success) {
          setAgentSuggestions(result.data);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [searchValue]);

  // Fetch parent suggestions based on search
  useEffect(() => {
    const fetchParents = async () => {
      if (parentSearchValue.length < 2) {
        setParentSuggestions([]);
        return;
      }

      setLoadingParents(true);
      try {
        const result = await Parse.Cloud.run("searchParentUsers", {
          search: parentSearchValue,
        });
        if (result.success) {
          setParentSuggestions(result.data);
        }
      } catch (error) {
        console.error("Error fetching parents:", error);
      } finally {
        setLoadingParents(false);
      }
    };

    const debounce = setTimeout(fetchParents, 300);
    return () => clearTimeout(debounce);
  }, [parentSearchValue]);

  const getDateRange = (range) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (range) {
      case "today": {
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        return { dateFrom: new Date(today), dateTo: endOfToday };
      }
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const endOfYesterday = new Date(yesterday);
        endOfYesterday.setHours(23, 59, 59, 999);
        return { dateFrom: yesterday, dateTo: endOfYesterday };
      }
      case "last7days": {
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        return { dateFrom: last7, dateTo: endOfToday };
      }
      case "thisMonth": {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        return { dateFrom: firstDay, dateTo: endOfToday };
      }
      case "custom":
        return { 
          dateFrom: customDateFrom ? new Date(customDateFrom) : null, 
          dateTo: customDateTo ? new Date(customDateTo) : null 
        };
      default:
        return { dateFrom: null, dateTo: null };
    }
  };

  const handleApply = () => {
    const filters = {};
    
    if (searchValue && searchValue !== "") {
      filters.agentName = searchValue;
    }

    const { dateFrom, dateTo } = getDateRange(dateRange);
    if (dateFrom) filters.dateFrom = dateFrom.toISOString();
    if (dateTo) filters.dateTo = dateTo.toISOString();
    
    // Holding % filter
    if (holdingRange && holdingRange !== "all") {
      if (holdingRange === "custom") {
        if (customHoldingMin !== "") filters.holdingMin = parseFloat(customHoldingMin);
        if (customHoldingMax !== "") filters.holdingMax = parseFloat(customHoldingMax);
      } else {
        // Parse range like "0-10", "10-20", etc.
        const [min, max] = holdingRange.split("-").map(Number);
        filters.holdingMin = min;
        filters.holdingMax = max;
      }
    }
    
    if (parentFilter && parentFilter.id) {
      filters.parentId = parentFilter.id;
    }

    setFilters(filters, {});
    onFiltersChange(filters);
  };

  const handleClear = () => {
    setSearchValue("");
    setDateRange("all");
    setCustomDateFrom("");
    setCustomDateTo("");
    setHoldingRange("all");
    setCustomHoldingMin("");
    setCustomHoldingMax("");
    setParentFilter(null);
    setParentSearchValue("");
    setFilters({}, {});
    onFiltersChange({});
  };

  return (
    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 2, width: "100%" }}>
      <Autocomplete
        freeSolo
        options={agentSuggestions}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
        inputValue={searchValue}
        onInputChange={(e, newValue) => setSearchValue(newValue)}
        onChange={(e, newValue) => {
          if (newValue && typeof newValue === "object") {
            setSearchValue(newValue.name);
          }
        }}
        loading={loadingSuggestions}
        sx={{ minWidth: 200 }}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            size="small"
            label="Search Agent Name"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingSuggestions ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              sx: {
                backgroundColor: "#333",
                color: "white",
              },
            }}
            InputLabelProps={{
              style: { color: "#ccc" },
            }}
          />
        )}
      />

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel sx={{ color: "#ccc" }}>Date Range</InputLabel>
        <Select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          label="Date Range"
          sx={{
            backgroundColor: "#333",
            color: "white",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.23)",
            },
          }}
        >
          <MenuItem value="all">All Time</MenuItem>
          <MenuItem value="today">Today</MenuItem>
          <MenuItem value="yesterday">Yesterday</MenuItem>
          <MenuItem value="last7days">Last 7 Days</MenuItem>
          <MenuItem value="thisMonth">This Month</MenuItem>
          <MenuItem value="custom">Custom Range</MenuItem>
        </Select>
      </FormControl>

      {dateRange === "custom" && (
        <>
          <MuiTextField
            size="small"
            type="date"
            label="From Date"
            value={customDateFrom}
            onChange={(e) => setCustomDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: "#ccc" } }}
            sx={{
              minWidth: 130,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#333",
                color: "white",
              },
            }}
          />
          <MuiTextField
            size="small"
            type="date"
            label="To Date"
            value={customDateTo}
            onChange={(e) => setCustomDateTo(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: "#ccc" } }}
            sx={{
              minWidth: 130,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#333",
                color: "white",
              },
            }}
          />
        </>
      )}

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel sx={{ color: "#ccc" }}>Holding %</InputLabel>
        <Select
          value={holdingRange}
          onChange={(e) => setHoldingRange(e.target.value)}
          label="Holding %"
          sx={{
            backgroundColor: "#333",
            color: "white",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.23)",
            },
          }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="0-20">0-20%</MenuItem>
          <MenuItem value="20-40">20-40%</MenuItem>
          <MenuItem value="40-60">40-60%</MenuItem>
          <MenuItem value="60-80">60-80%</MenuItem>
          <MenuItem value="80-100">80-100%</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </Select>
      </FormControl>

      {holdingRange === "custom" && (
        <>
          <MuiTextField
            size="small"
            type="number"
            label="Min %"
            value={customHoldingMin}
            onChange={(e) => setCustomHoldingMin(e.target.value)}
            InputLabelProps={{ style: { color: "#ccc" } }}
            sx={{
              minWidth: 80,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#333",
                color: "white",
              },
            }}
          />
          <MuiTextField
            size="small"
            type="number"
            label="Max %"
            value={customHoldingMax}
            onChange={(e) => setCustomHoldingMax(e.target.value)}
            InputLabelProps={{ style: { color: "#ccc" } }}
            sx={{
              minWidth: 80,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#333",
                color: "white",
              },
            }}
          />
        </>
      )}

      <Autocomplete
        freeSolo={false}
        options={parentSuggestions}
        getOptionLabel={(option) => 
          typeof option === "string" ? option : `${option.name} (${option.role})`
        }
        value={parentFilter}
        inputValue={parentSearchValue}
        onInputChange={(e, newValue) => setParentSearchValue(newValue)}
        onChange={(e, newValue) => {
          setParentFilter(newValue);
        }}
        loading={loadingParents}
        sx={{ minWidth: 200 }}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            size="small"
            label="Filter by Parent"
            placeholder="Type to search..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingParents ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              sx: {
                backgroundColor: "#333",
                color: "white",
              },
            }}
            InputLabelProps={{
              style: { color: "#ccc" },
            }}
          />
        )}
      />

      <Button
        variant="contained"
        onClick={handleApply}
        sx={{
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFB300",
          },
        }}
      >
        Apply Filters
      </Button>
      <Button
        variant="outlined"
        onClick={handleClear}
        sx={{
          borderColor: "#FFC107",
          color: "#fff",
          textTransform: "none",
          "&:hover": {
            borderColor: "#FFB300",
            backgroundColor: "rgba(255, 193, 7, 0.1)",
          },
        }}
      >
        Clear All
      </Button>
    </Box>
  );
};

// List Actions Component
const ListActions = ({
  onExport,
  onColumnSelect,
  selectedCount,
  exporting,
  onFiltersChange,
}) => (
  <TopToolbar
    sx={{
      justifyContent: "space-between",
      alignItems: "flex-start",
      width: "100%",
      flexDirection: "column",
      gap: 2,
    }}
  >
    {/* Filters Section */}
    <Box sx={{ width: "100%" }}>
      <CustomFilter onFiltersChange={onFiltersChange} />
    </Box>

    {/* Right Side - Columns and Export Excel */}
    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" justifyContent="flex-end" width="100%">
      <Button
        variant="contained"
        onClick={onColumnSelect}
        sx={{
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFB300",
          },
        }}
        startIcon={<ViewColumn />}
      >
        Columns ({selectedCount})
      </Button>

      <Button
        variant="contained"
        onClick={onExport}
        sx={{
          background: "#4CAF50",
          color: "white",
          textTransform: "none",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: "#45a049",
          },
          "&:disabled": {
            backgroundColor: "#666",
            color: "#999",
          },
        }}
        disabled={exporting}
        startIcon={<Download />}
      >
        {exporting ? "Exporting..." : "Export Excel"}
      </Button>
    </Box>
  </TopToolbar>
);

// Custom Pagination Component
const PostPagination = () => {
  const { total, page, perPage, setPage, setPerPage } = useListContext();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100% !important",
        margin: "16px 0px",
      }}
    >
      <CustomPagination
        page={page}
        perPage={perPage}
        total={total}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </Box>
  );
};

export const AgentReport = (props) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Column selection state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const defaults = {};
    Object.values(COLUMN_GROUPS)
      .flat()
      .forEach((col) => {
        defaults[col.id] = col.default;
      });
    
    const saved = localStorage.getItem("agentReportColumns");
    if (saved) {
      try {
        const savedColumns = JSON.parse(saved);
        // Merge: for each column, use saved value if exists, otherwise use default
        const merged = {};
        Object.values(COLUMN_GROUPS)
          .flat()
          .forEach((col) => {
            merged[col.id] = savedColumns.hasOwnProperty(col.id) 
              ? savedColumns[col.id] 
              : col.default;
          });
        return merged;
      } catch (e) {
        console.error("Error parsing saved columns:", e);
        return defaults;
      }
    }
    return defaults;
  });

  const [exporting, setExporting] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});

  // Column selection handlers
  const handleColumnToggle = (columnId) => {
    const newSelection = {
      ...selectedColumns,
      [columnId]: !selectedColumns[columnId],
    };
    setSelectedColumns(newSelection);
    localStorage.setItem("agentReportColumns", JSON.stringify(newSelection));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    Object.values(COLUMN_GROUPS)
      .flat()
      .forEach((col) => {
        allSelected[col.id] = true;
      });
    setSelectedColumns(allSelected);
    localStorage.setItem("agentReportColumns", JSON.stringify(allSelected));
  };

  const handleDeselectAll = () => {
    const noneSelected = {};
    Object.values(COLUMN_GROUPS)
      .flat()
      .forEach((col) => {
        noneSelected[col.id] = false;
      });
    setSelectedColumns(noneSelected);
    localStorage.setItem("agentReportColumns", JSON.stringify(noneSelected));
  };

  const handleResetToDefault = () => {
    const defaults = {};
    Object.values(COLUMN_GROUPS)
      .flat()
      .forEach((col) => {
        defaults[col.id] = col.default;
      });
    setSelectedColumns(defaults);
    localStorage.setItem("agentReportColumns", JSON.stringify(defaults));
  };

  // Export to Excel
  const handleExport = useCallback(async () => {
    try {
      setExporting(true);

      const response = await Parse.Cloud.run("getAgentAccountReport", {
        page: 1,
        limit: 10000,
        search: currentFilters.agentName || "",
        dateFrom: currentFilters.dateFrom || null,
        dateTo: currentFilters.dateTo || null,
        role: currentFilters.role || null,
        parentId: currentFilters.parentId || null,
        sortBy: "agentName",
        sortOrder: "asc",
      });

      if (!response.success) {
        alert("Failed to export data");
        return;
      }

      // Filter columns based on selection
      const excelData = response.data.map((row, index) => {
        const filteredRow = { "Sr No": row.srNo };

        Object.values(COLUMN_GROUPS)
          .flat()
          .forEach((col) => {
            if (selectedColumns[col.id]) {
              filteredRow[col.label] = formatCellValue(row[col.id]);
            }
          });

        return filteredRow;
      });

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Agent Report");

      const filename = `Agent_Report_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      XLSX.writeFile(wb, filename);

      console.log("✅ Excel exported:", filename);
    } catch (err) {
      console.error("❌ Export error:", err);
      alert(`Export Error: ${err.message}`);
    } finally {
      setExporting(false);
    }
  }, [selectedColumns, currentFilters]);

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (value instanceof Date) {
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return value;
  };

  const getVisibleColumns = () => {
    return Object.values(COLUMN_GROUPS)
      .flat()
      .filter((col) => selectedColumns[col.id]);
  };

  const selectedCount = Object.values(selectedColumns).filter(Boolean).length;

  return (
    <Box
      sx={{
        mt: 4,
        backgroundColor: "#242424",
        borderRadius: 2,
        boxShadow: "none !important",
        padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
      }}
    >
      <List
        {...props}
        resource="agentAccountReport"
        pagination={<PostPagination />}
        actions={
          <ListActions
            onExport={handleExport}
            onColumnSelect={() => setDrawerOpen(true)}
            selectedCount={selectedCount}
            exporting={exporting}
            onFiltersChange={setCurrentFilters}
          />
        }
        sort={{ field: "agentName", order: "ASC" }}
        sx={{ "& .RaList-content": { boxShadow: "none" } }}
      >
        <Datagrid
          bulkActionButtons={false}
          sx={{
            "& .column-undefined": { display: "none" },
            "& .RaDatagrid-table": {
              backgroundColor: "#2a2a2a",
            },
            "& .RaDatagrid-headerCell": {
              color: "#FFC107 !important",
              fontWeight: "700",
              fontSize: "12px",
              letterSpacing: "0.5px",
              backgroundColor: "#2a2a2a",
            },
            "& .RaDatagrid-rowCell": {
              color: "white",
              fontSize: "13px",
              borderBottom: "1px solid #333",
            },
            "& .RaDatagrid-row:hover": {
              backgroundColor: "#333",
            },
          }}
        >
          <NumberField source="srNo" label="Sr No" textAlign="left" />

          {/* Agent Information */}
          {selectedColumns.agentName && (
            <FunctionField
              label="Agent Name"
              source="agentName"
              render={(record) => (
                <Tooltip title={record.agentName || ''} arrow>
                  <span>{record.agentName}</span>
                </Tooltip>
              )}
            />
          )}
          {selectedColumns.agentFirstRechargeDate && (
            <DateField
              source="agentFirstRechargeDate"
              label="First Recharge Date"
            />
          )}
          {selectedColumns.agentFirstRechargeAmount && (
            <NumberField
              source="agentFirstRechargeAmount"
              label="First Recharge Amount"
            />
          )}
          {selectedColumns.agentFirstRechargeDoneBy && (
            <FunctionField
              label="First Recharge Done By"
              source="agentFirstRechargeDoneBy"
              render={(record) => (
                <Tooltip title={record.agentFirstRechargeDoneBy || ''} arrow>
                  <span>{record.agentFirstRechargeDoneBy}</span>
                </Tooltip>
              )}
            />
          )}
          {selectedColumns.agentTotalRecharge && (
            <NumberField source="agentTotalRecharge" label="Total Recharge" />
          )}
          {selectedColumns.agentFreeCredit && (
            <NumberField source="agentFreeCredit" label="Free Credit" />
          )}
          {selectedColumns.agentRemainingCredit && (
            <NumberField
              source="agentRemainingCredit"
              label="Remaining Credit"
            />
          )}
          {selectedColumns.agentLastRechargeDate && (
            <DateField
              source="agentLastRechargeDate"
              label="Last Recharge Date"
            />
          )}
          {selectedColumns.agentLastRedeemDate && (
            <DateField source="agentLastRedeemDate" label="Last Redeem Date" />
          )}
          {selectedColumns.agentCurrentBalance && (
            <NumberField source="agentCurrentBalance" label="Current Balance" />
          )}

          {/* Player Metrics */}
          {selectedColumns.totalPlayerRecharge && (
            <NumberField
              source="totalPlayerRecharge"
              label="Total Player Recharge"
            />
          )}
          {selectedColumns.totalPlayerRedeem && (
            <NumberField
              source="totalPlayerRedeem"
              label="Total Player Redeem"
            />
          )}

          {/* Calculated Metrics */}
          {selectedColumns.agentIncome && (
            <NumberField source="agentIncome" label="Income" />
          )}
          {selectedColumns.profitLossStatus && (
            <FunctionField
              label="Profit/Loss"
              source="profitLossStatus"
              textAlign="left"
              render={(record) => {
                const income = parseFloat(record.agentIncome || 0);
                const formattedIncome = income.toLocaleString('en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                });
                return (
                  <Tooltip title={formattedIncome} arrow>
                    <span style={{ 
                      color: income >= 0 ? '#4CAF50' : '#F44336',
                      fontWeight: 'bold'
                    }}>
                      {formattedIncome}
                    </span>
                  </Tooltip>
                );
              }}
            />
          )}
          {selectedColumns.agentHolding && (
            <FunctionField
              label="Holding %"
              source="agentHolding"
              render={(record) => `${parseFloat(record.agentHolding || 0).toFixed(2)}%`}
              textAlign="left"
            />
          )}

          {/* Sub-Distributor */}
          {selectedColumns.subDistributorName && (
            <TextField source="subDistributorName" label="Sub-Distributor" />
          )}
          {selectedColumns.subDistributorFirstRechargeDate && (
            <DateField
              source="subDistributorFirstRechargeDate"
              label="Sub-Dist First Recharge Date"
            />
          )}
          {selectedColumns.subDistributorFirstRechargeAmount && (
            <NumberField
              source="subDistributorFirstRechargeAmount"
              label="Sub-Dist First Recharge Amount"
            />
          )}
          {selectedColumns.subDistributorFirstRechargeDoneBy && (
            <TextField
              source="subDistributorFirstRechargeDoneBy"
              label="Sub-Dist First Recharge By"
            />
          )}
          {selectedColumns.subDistributorTotalRecharge && (
            <NumberField
              source="subDistributorTotalRecharge"
              label="Sub-Dist Total Recharge"
            />
          )}
          {selectedColumns.subDistributorCurrentBalance && (
            <NumberField
              source="subDistributorCurrentBalance"
              label="Sub-Dist Balance"
            />
          )}

          {/* Distributor */}
          {selectedColumns.distributorName && (
            <TextField source="distributorName" label="Distributor" />
          )}
          {selectedColumns.distributorFirstRechargeDate && (
            <DateField
              source="distributorFirstRechargeDate"
              label="Dist First Recharge Date"
            />
          )}
          {selectedColumns.distributorFirstRechargeAmount && (
            <NumberField
              source="distributorFirstRechargeAmount"
              label="Dist First Recharge Amount"
            />
          )}
          {selectedColumns.distributorFirstRechargeDoneBy && (
            <TextField
              source="distributorFirstRechargeDoneBy"
              label="Dist First Recharge By"
            />
          )}
          {selectedColumns.distributorTotalRecharge && (
            <NumberField
              source="distributorTotalRecharge"
              label="Dist Total Recharge"
            />
          )}
          {selectedColumns.distributorCurrentBalance && (
            <NumberField
              source="distributorCurrentBalance"
              label="Dist Balance"
            />
          )}

          {/* Master-Distributor */}
          {selectedColumns.masterDistributorName && (
            <TextField
              source="masterDistributorName"
              label="Master-Distributor"
            />
          )}
          {selectedColumns.masterDistributorFirstRechargeDate && (
            <DateField
              source="masterDistributorFirstRechargeDate"
              label="Master-Dist First Recharge Date"
            />
          )}
          {selectedColumns.masterDistributorFirstRechargeAmount && (
            <NumberField
              source="masterDistributorFirstRechargeAmount"
              label="Master-Dist First Recharge Amount"
            />
          )}
          {selectedColumns.masterDistributorFirstRechargeDoneBy && (
            <TextField
              source="masterDistributorFirstRechargeDoneBy"
              label="Master-Dist First Recharge By"
            />
          )}
          {selectedColumns.masterDistributorTotalRecharge && (
            <NumberField
              source="masterDistributorTotalRecharge"
              label="Master-Dist Total Recharge"
            />
          )}
          {selectedColumns.masterDistributorCurrentBalance && (
            <NumberField
              source="masterDistributorCurrentBalance"
              label="Master-Dist Balance"
            />
          )}

          {/* Game-Owner */}
          {selectedColumns.gameOwnerName && (
            <TextField source="gameOwnerName" label="Game Owner" />
          )}
          {selectedColumns.gameOwnerFirstRechargeDate && (
            <DateField
              source="gameOwnerFirstRechargeDate"
              label="Owner First Recharge Date"
            />
          )}
          {selectedColumns.gameOwnerFirstRechargeAmount && (
            <NumberField
              source="gameOwnerFirstRechargeAmount"
              label="Owner First Recharge Amount"
            />
          )}
          {selectedColumns.gameOwnerFirstRechargeDoneBy && (
            <TextField
              source="gameOwnerFirstRechargeDoneBy"
              label="Owner First Recharge By"
            />
          )}
          {selectedColumns.gameOwnerTotalRecharge && (
            <NumberField
              source="gameOwnerTotalRecharge"
              label="Owner Total Recharge"
            />
          )}
          {selectedColumns.gameOwnerCurrentBalance && (
            <NumberField
              source="gameOwnerCurrentBalance"
              label="Owner Balance"
            />
          )}

          {/* Super User */}
          {selectedColumns.superUserName && (
            <TextField source="superUserName" label="Super User" />
          )}
        </Datagrid>
      </List>

      {/* Column Selection Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: 350, p: 3, background: "#1c1c1c" },
        }}
      >
        <Typography variant="h6" color="white" sx={{ mb: 2 }}>
          Select Columns
        </Typography>

        <Box display="flex" gap={1} mb={2}>
          <Button size="small" onClick={handleSelectAll} variant="outlined">
            Select All
          </Button>
          <Button size="small" onClick={handleDeselectAll} variant="outlined">
            Deselect All
          </Button>
          <Button
            size="small"
            onClick={handleResetToDefault}
            variant="outlined"
          >
            Reset
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {Object.entries(COLUMN_GROUPS).map(([groupName, columns]) => (
          <Box key={groupName} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              {groupName}
            </Typography>
            <FormGroup>
              {columns.map((col) => (
                <FormControlLabel
                  key={col.id}
                  control={
                    <Checkbox
                      checked={selectedColumns[col.id] || false}
                      onChange={() => handleColumnToggle(col.id)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" color="white">
                      {col.label}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Box>
        ))}
      </Drawer>
    </Box>
  );
};
